import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { 
  Invoice, InvoiceFilter, CreateInvoiceRequest, 
  UpdateInvoiceRequest, UpdateInvoicePaymentRequest, PagedResult 
} from '../types/api';

const INVOICES_KEY = 'invoices';

export function useInvoices(filter: InvoiceFilter = {}) {
  const params = new URLSearchParams();
  if (filter.pageNumber) params.append('pageNumber', filter.pageNumber.toString());
  if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
  if (filter.customerId) params.append('customerId', filter.customerId.toString());
  if (filter.shipmentId) params.append('shipmentId', filter.shipmentId.toString());
  if (filter.paymentStatus) params.append('paymentStatus', filter.paymentStatus);
  if (filter.fromDate) params.append('fromDate', filter.fromDate);
  if (filter.toDate) params.append('toDate', filter.toDate);

  return useQuery<PagedResult<Invoice>>({
    queryKey: [INVOICES_KEY, filter],
    queryFn: () => api.get(`/Invoices?${params.toString()}`),
  });
}

export function useInvoice(id: number) {
  return useQuery<Invoice>({
    queryKey: [INVOICES_KEY, id],
    queryFn: () => api.get(`/Invoices/${id}`),
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInvoiceRequest) => api.post<Invoice>('/Invoices', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INVOICES_KEY] }),
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateInvoiceRequest }) => 
      api.put<Invoice>(`/Invoices/${id}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INVOICES_KEY] }),
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/Invoices/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INVOICES_KEY] }),
  });
}

export function useUpdateInvoicePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateInvoicePaymentRequest }) => 
      api.put<Invoice>(`/Invoices/${id}/payment`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INVOICES_KEY] }),
  });
}
